/**
 * Shared types of the engine. The whole `GameState` is plain JSON (objects, arrays, strings,
 * booleans, integers): no Map, Set, class, undefined-as-value or float. That is what makes a
 * game serialisable and replayable from `seed + config + action log`.
 */

export type PlayerId = string;
export type RoomId = string;
export type PuzzleId = string;

export type Role = 'mastermind' | 'locksmith' | 'hacker' | 'lookout' | 'driver';
export const ROLES: readonly Role[] = ['mastermind', 'locksmith', 'hacker', 'lookout', 'driver'];

/** Roles in play for 3, 4 and 5 players (GDD §3.1). */
export const ROLES_BY_PLAYER_COUNT: Record<number, readonly Role[]> = {
  3: ['mastermind', 'locksmith', 'hacker'],
  4: ['mastermind', 'locksmith', 'hacker', 'lookout'],
  5: ['mastermind', 'locksmith', 'hacker', 'lookout', 'driver'],
};

export type Difficulty = 'easy' | 'normal' | 'pro' | 'legend';
export type MapId = 'bank' | 'museum' | 'villa' | 'express';
export type EntranceId = 'roof' | 'sewer' | 'service';
export type GearId = 'proPick' | 'jammer' | 'rope' | 'disguise' | 'drone' | 'emp';
export type Language = 'it' | 'en';
export type InfiltratorSetting = 'never' | 'always' | 'normal';

export type PuzzleType =
  'lock' | 'safe' | 'panel' | 'cameras' | 'laser' | 'password' | 'corridor' | 'escape' | 'vault';

export type SabotageKind = 'tipoff' | 'tamper' | 'rush';
/** What every player is told when something happens: identical for events and sabotage. */
export type NoticeKind = 'suspiciousGuard' | 'interference' | 'patrolEarly';
export const NOTICE_FOR_SABOTAGE: Record<SabotageKind, NoticeKind> = {
  tipoff: 'suspiciousGuard',
  tamper: 'interference',
  rush: 'patrolEarly',
};
export const SABOTAGE_FOR_NOTICE: Record<NoticeKind, SabotageKind> = {
  suspiciousGuard: 'tipoff',
  interference: 'tamper',
  patrolEarly: 'rush',
};

export type Phase =
  'briefing' | 'planning' | 'execution' | 'escape' | 'hideout' | 'reveal' | 'ended';

export type HideoutStage = 'discussion' | 'accusation' | 'split';

// ---------------------------------------------------------------------------------------
// Configuration

export interface PlayerConfig {
  id: PlayerId;
  name: string;
}

export interface GameConfig {
  players: PlayerConfig[];
  mapId: MapId;
  difficulty: Difficulty;
  infiltrator: InfiltratorSetting;
  /** Language of the password vocabulary (GDD §16, item 23). */
  language: Language;
  /** Overrides the nominal execution timer (private rooms). */
  timerMs?: number;
  /** Explicit roles (private-room draft). Must be a permutation of the roles for the count. */
  roles?: Record<PlayerId, Role>;
  /** Who may be the infiltrator (solo play: a bot, never the human). Everybody when absent. */
  infiltratorEligible?: PlayerId[];
}

// ---------------------------------------------------------------------------------------
// Plan

export interface Detour {
  /** Core room on the route the detour hangs from. */
  anchor: RoomId;
  /** Optional alcove (dead end) adjacent to the anchor. */
  room: RoomId;
}

export interface Plan {
  entrance: EntranceId;
  /** Core rooms from the entrance room to the vault. */
  route: RoomId[];
  detours: Detour[];
  gear: GearId[];
}

export type PlanSubstage = 'awaiting' | 'voting' | 'tiebreak' | 'done';

// ---------------------------------------------------------------------------------------
// Players

export interface Powers {
  planB: number;
  loops: number;
  coins: number;
  petards: number;
}

export interface PlayerState {
  id: PlayerId;
  name: string;
  role: Role;
  /** Seat order, 0-based. Used for "next player" rules. */
  seat: number;
  expelled: boolean;
  powers: Powers;
  /** Suspicion pin on the hideout board. */
  pin: PlayerId | null;
}

// ---------------------------------------------------------------------------------------
// Puzzles (generic envelope; each module owns the shape of params/state/clues)

export interface Clue {
  id: string;
  kind: string;
  /** Role that sees this clue. */
  holder: Role;
  /** The truth. Never leaves the server. */
  value: unknown;
  /** Set by a sabotage or a random event: what the holder actually sees. */
  tampered?: { value: unknown; by: PlayerId | 'event'; atMs: number };
}

export interface PuzzleInstance {
  id: PuzzleId;
  type: PuzzleType;
  room: RoomId;
  level: number;
  solved: boolean;
  solvedAtMs: number | null;
  /** Secret generation data, including the solution. */
  params: unknown;
  /** Mutable progress. */
  state: unknown;
  clues: Clue[];
  /** Roles allowed to act on it. */
  solvers: Role[];
}

// ---------------------------------------------------------------------------------------
// Execution

export type Stop =
  | {
      kind: 'room';
      room: RoomId;
      optional: boolean;
      skipped: boolean;
      puzzles: PuzzleId[];
      started: boolean;
      /** The vault room: its code puzzle is created once the pre-puzzle (if any) is solved. */
      vaultNext: boolean;
    }
  | {
      kind: 'corridor';
      from: RoomId;
      to: RoomId;
      optional: boolean;
      skipped: boolean;
      puzzle: PuzzleId | null;
    }
  | { kind: 'rope'; from: RoomId; to: RoomId }
  | { kind: 'return'; from: RoomId; to: RoomId; skipped: boolean };

export interface ScheduledEvent {
  /** Execution time (ms of real elapsed execution) at which it fires. */
  atMs: number;
  kind: NoticeKind;
  fired: boolean;
  /** Radio bulletin time for the driver, or null when this event is not announced. */
  bulletinAtMs: number | null;
}

export interface PendingEffect {
  atMs: number;
  kind: SabotageKind;
  cause: 'random' | 'sabotage';
  by: PlayerId | null;
}

export interface LastError {
  /** Alarm tenths that the error added (already scaled). */
  tenths: number;
  atMs: number;
  puzzleId: PuzzleId | null;
  /** How to give back the attempt (module-specific). */
  undo: { kind: string; data?: unknown } | null;
  /** Used so a second Plan B cannot undo the same error. */
  used: boolean;
}

export interface ExecState {
  entrance: EntranceId;
  /** Level adjustment for the first puzzle of the heist (sewers +1, service door −1), then 0. */
  entranceLevelAdjust: number;
  plan: Plan;
  /** Remaining timer in microseconds, so that rate multipliers stay integral. */
  timerUs: number;
  /** Real elapsed execution time in ms (not scaled by the timer rate). */
  elapsedMs: number;
  rushUntilMs: number;
  loopUntilMs: number;
  droneUntilMs: number;
  tunnelUntilMs: number;
  /** −1 when the map has no tunnels. */
  nextTunnelAtMs: number;
  policeAlerted: boolean;
  optionalClosed: boolean;
  itinerary: Stop[];
  stopIndex: number;
  puzzles: Record<PuzzleId, PuzzleInstance>;
  gearLeft: GearId[];
  disguiseUsed: boolean;
  proPickUsed: boolean;
  completedOptional: RoomId[];
  lastError: LastError | null;
  scheduled: ScheduledEvent[];
  pending: PendingEffect[];
  lastNoticeAtMs: number;
  /** Time accumulator for the camera drip. */
  dripAccMs: number;
  /** Escape puzzle id, once the escape began. */
  escapePuzzle: PuzzleId | null;
  /** Alarm (tenths) when the escape was solved. */
  alarmFinal: number | null;
  /** Ordered ids of every puzzle ever created, so ids stay unique and deterministic. */
  puzzleCounter: number;
}

// ---------------------------------------------------------------------------------------
// Hideout and result

export type Shares = Record<PlayerId, number>;

export interface HideoutState {
  stage: HideoutStage;
  stageStartedMs: number;
  /** Secret ballots: voter → target ('none' or a player). */
  accusations: Record<PlayerId, PlayerId | 'none'>;
  expelled: PlayerId | null;
  accusationResolved: boolean;
  /** Split negotiation. */
  proposer: PlayerId | null;
  proposal: Shares | null;
  splitVotes: Record<PlayerId, boolean>;
  splitRejections: number;
  /** `true` once the proposal phase has a proposal and is in the voting sub-stage. */
  splitVoting: boolean;
  finalShares: Shares | null;
  /** Equal split forced after too many rejections. */
  equalSplit: boolean;
}

export interface GameResult {
  success: boolean;
  /** Reason when the heist failed. */
  failure: 'alarm' | 'time' | 'vault' | null;
  infiltrator: PlayerId | null;
  expelled: PlayerId | null;
  winners: PlayerId[];
  /** Loot after the alarm penalty, in millions of lire. */
  loot: number;
  lootBase: number;
  lootExtras: number;
  penaltyPercent: number;
  plannedLoot: number;
  alarmFinal: number;
  shares: Shares;
  /** Score in millions of lire for each player (winners only). */
  scores: Record<PlayerId, number>;
}

// ---------------------------------------------------------------------------------------
// Log and events

export type EventAudience = 'all' | PlayerId[];

export interface GameEvent {
  /** Monotonic id inside the game. */
  id: number;
  type: string;
  atMs: number;
  audience: EventAudience;
  data: Record<string, unknown>;
  /** Only shown from the reveal onwards (e.g. whether a notice was a sabotage). */
  secret?: Record<string, unknown>;
}

export interface ActionRecord {
  seq: number;
  by: PlayerId | 'server';
  action: Action;
  atMs: number;
}

// ---------------------------------------------------------------------------------------
// Game state

export interface PlanningState {
  substage: PlanSubstage;
  /** Clock time at which the current substage began (vote and tie-break timeouts). */
  stageStartedMs: number;
  /** Number of proposals rejected so far. */
  rejections: number;
  proposal: Plan | null;
  votes: Record<PlayerId, boolean>;
  approved: Plan | null;
  /** The mastermind decided alone after too many rejections. */
  decidedAlone: boolean;
}

export interface InfiltratorState {
  id: PlayerId | null;
  /** `true` while the sabotage is still unused. */
  available: Record<SabotageKind, boolean>;
}

export interface GameState {
  rulesVersion: number;
  config: GameConfig;
  /** Root seed. Secret. */
  seed: string;
  /** Total game time, advanced only by `tick`. */
  clockMs: number;
  phase: Phase;
  phaseStartedMs: number;
  /** Next event id. */
  nextEventId: number;
  nextActionSeq: number;
  players: PlayerState[];
  infiltrator: InfiltratorState;
  /** Briefing: who pressed ready. */
  ready: PlayerId[];
  planning: PlanningState;
  /** Alarm in tenths of a percent point. */
  alarm: number;
  exec: ExecState | null;
  hideout: HideoutState | null;
  result: GameResult | null;
  /** Full event log (server side; filtered per player by `getPlayerView`). */
  log: GameEvent[];
}

// ---------------------------------------------------------------------------------------
// Actions

export interface PhraseRef {
  id: string;
  params: (number | string)[];
}

export type PingKind = 'here' | 'danger' | 'wait' | 'go';

export type Direction8 = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
export type Direction4 = 'n' | 'e' | 's' | 'w';

/** One discriminated union for every puzzle interaction. */
export type PuzzleAct =
  | { kind: 'lock.set'; pin: number; height: number }
  | { kind: 'safe.try'; digits: number[] }
  | { kind: 'panel.act'; wire: number; act: 'cut' | 'connect' }
  | { kind: 'cameras.rotate'; camera: number; orientation: Direction8 }
  | { kind: 'cameras.verify' }
  | { kind: 'laser.flip'; mirror: number }
  | { kind: 'laser.fire' }
  | { kind: 'password.try'; word: number }
  | { kind: 'corridor.move'; dir: Direction4 | 'stay' }
  | { kind: 'corridor.coin'; x: number; y: number }
  | { kind: 'corridor.petard'; x: number; y: number }
  | { kind: 'escape.move'; block: number; delta: number }
  | { kind: 'vault.set'; pos: number; symbol: number }
  | { kind: 'vault.confirm' }
  | { kind: 'vault.unconfirm' };

export type Action =
  | { type: 'tick'; dtMs: number }
  | { type: 'ready' }
  | { type: 'plan.propose'; plan: Plan }
  | { type: 'plan.vote'; approve: boolean }
  | { type: 'plan.tiebreak'; decision: 'approve' | 'revise' }
  | { type: 'puzzle.act'; puzzleId: PuzzleId; act: PuzzleAct }
  | { type: 'gear.use'; gear: GearId; puzzleId?: PuzzleId }
  | { type: 'power.use'; power: 'planB' | 'loop' }
  | { type: 'exec.skipOptional' }
  | { type: 'sabotage.use'; kind: SabotageKind }
  | { type: 'chat.quick'; phrase: PhraseRef }
  | { type: 'ping'; kind: PingKind; room: RoomId | null }
  | { type: 'pin.place'; target: PlayerId | null }
  | { type: 'accuse.vote'; target: PlayerId | 'none' }
  | { type: 'split.propose'; shares: Shares }
  | { type: 'split.vote'; approve: boolean }
  | { type: 'reveal.continue' };

export interface ActionEnvelope {
  /** Who acts. `server` is required for `tick`. */
  by: PlayerId | 'server';
  action: Action;
}

export type ActionErrorCode =
  | 'gameOver'
  | 'wrongPhase'
  | 'notAllowed'
  | 'invalid'
  | 'unknownPuzzle'
  | 'puzzleNotActive'
  | 'puzzleSolved'
  | 'illegalMove'
  | 'noCharges'
  | 'cooldown'
  | 'nothingToDo'
  | 'tooLate';

export interface ActionError {
  code: ActionErrorCode;
  message: string;
}

export interface ApplyOutput {
  state: GameState;
  events: GameEvent[];
}
